/* eslint-disable no-undef */
import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!supabaseUrl || !supabaseKey) {
  console.error(
    "Supabase credentials missing. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.",
  );
}

export const supabase = createClient(
  supabaseUrl || "", 
  supabaseKey || "",
  {
    auth: {
      persistSession: true,
      storageKey: 'CouponLa-auth',
      autoRefreshToken: true,
      detectSessionInUrl: true,
      debug: false
    }
  }
);

// Hybrid caching integration helpers (Cloudflare KV + Upstash Redis)
const isCloudflare = typeof globalThis !== 'undefined' && !!globalThis.KV_CouponLa;
const kv = isCloudflare ? globalThis.KV_CouponLa : null;
const KV_TTL = 300; // 5 minutes in seconds

/**
 * Get value from cache (trying Redis first for better performance, then KV)
 * KV is preferred for globally distributed reads
 */
async function kvGet(key) {
  // First try Redis for faster access
  try {
    // Redis is faster but might not be globally distributed
    // For read-heavy operations, using Redis first can improve performance
    const { redisGet } = await import('./upstash.js');
    const redisValue = await redisGet(key);
    if (redisValue !== null) {
      return redisValue;
    }
  } catch (redisError) {
    // Redis error - fall back to KV
    console.warn('Redis get error (falling back to KV):', redisError);
  }

  // Fall back to KV
  if (!kv) return null;
  try {
    const value = await kv.get(key);
    // Track the KV read operation for monitoring
    const { trackKVRead } = await import('./cacheMonitoring.js');
    trackKVRead('cache').catch(err => console.warn('Error tracking KV read:', err));
    return value ? JSON.parse(value) : null;
  } catch (e) {
    console.warn('KV get error:', e);
    return null;
  }
}

/**
 * Set value in cache with specific strategy based on frequency of writes
 * High write frequency: Redis only (to reduce KV write operations)
 * Low write frequency: KV and Redis (for global distribution)
 */
async function kvSet(key, value, ttl = KV_TTL) {
  // Determine if this is a high-write frequency key
  const highWriteFrequency = key.startsWith('rate:') || key.startsWith('error:');
  try {
    // Always write to Redis
    const { redisSet } = await import('./upstash.js');
    await redisSet(key, value, ttl);
    // Only write to KV for low-frequency write operations
    if (!highWriteFrequency && kv) {
      await kv.put(key, JSON.stringify(value), { expirationTtl: ttl });
      // Track the KV write operation for monitoring
      const { trackKVWrite } = await import('./cacheMonitoring.js');
      trackKVWrite('set', 'cache').catch(err => console.warn('Error tracking KV write:', err));
    }
  } catch (e) {
    console.warn('Cache set error:', e);
  }
}

/**
 * Delete value from both Redis and KV caches
 */
async function kvDelete(key) {
  try {
    // Always delete from Redis
    const { redisDelete } = await import('./upstash.js');
    await redisDelete(key);
    // Also delete from KV if available
    if (kv) {
      await kv.delete(key);
      // Track the KV write operation for monitoring
      const { trackKVWrite } = await import('./cacheMonitoring.js');
      trackKVWrite('delete', 'cache').catch(err => console.warn('Error tracking KV write:', err));
    }
  } catch (e) {
    console.warn('Cache delete error:', e);
  }
}

// Authentication helpers
export const auth = {
  // Register a new user
  async signUp(email, password) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    return { data, error };
  },
  
  // Check if user is in admin_users table
  async checkAdminStatus(userId) {
    if (!userId) return { isAdmin: false, isSuperAdmin: false };
    
    const { data, error } = await supabase
      .from('admin_users')
      .select('id, is_super_admin')
      .eq('id', userId)
      .single();

    if (error || !data) {
      return { isAdmin: false, isSuperAdmin: false };
    }
    
    return { 
      isAdmin: true, 
      isSuperAdmin: data.is_super_admin === true,
      adminData: data
    };
  },

  // Log in a user
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // Clear any old session errors
    if (data && data.session) {
      // Session established successfully
      // This will help debug authentication issues
      localStorage.setItem("lastLoginTime", new Date().toISOString());
    }
    
    return { data, error };
  },

  // Log out a user
  async signOut() {
    localStorage.removeItem("lastLoginTime");
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  // Get current user session
  async getSession() {
    const { data, error } = await supabase.auth.getSession();
    return { data, error };
  },

  // Get current user
  async getUser() {
    
    try {
      const { data, error } = await supabase.auth.getUser();
      
      if (error) {
        console.error("Auth: Error fetching user", error);
        return { user: null, error };
      }
      
      const user = data?.user;
      
      if (!user) {
        return { user: null, error: null };
      }
      
      return { user, error: null };
    } catch (err) {
      console.error("Auth: Exception fetching user", err);
      return { user: null, error: err };
    }
  },
  
  // Reset password with magic link
  async resetPassword(email) {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { data, error };
  },
  
  // Update user data
  async updateUser(userData) {
    const { data, error } = await supabase.auth.updateUser(userData);
    return { data, error };
  },
};

// Data service for coupons
export const couponsService = {
  /**
   * Fetch all coupons with their categories (many-to-many).
   * Supports filtering by store, category, featured, exclusive, and active status.
   * @param {Object} filters - Filtering options
   * @param {number} page - Page number
   * @param {number} limit - Page size
   */
  async getAll(filters = {}, page = 1, limit = 20) {
    // Only cache for simple store/category queries (no pagination, no search, no user-specific filters)
    let cacheKey = null;
    if (isCloudflare && page === 1 && limit === 20 && Object.keys(filters).length === 1) {
      if (filters.storeId) cacheKey = `coupons:store:${filters.storeId}`;
      if (filters.categoryId) cacheKey = `coupons:category:${filters.categoryId}`;
    }
    if (cacheKey) {
      const cached = await kvGet(cacheKey);
      if (cached) {
        console.log('Using KV cached coupons for', cacheKey);
        return { data: cached, error: null, count: cached.length };
      }
    }
    let selectStr = `*, store:stores(*), coupon_categories:coupon_categories(category_id, category:categories(*))`;
    if (filters.categoryId) {
      // Use inner join to filter by category via join table
      selectStr = `*, store:stores(*), coupon_categories!inner(category_id, category:categories(*))`;
    }
    let query = supabase
      .from("coupons")
      .select(selectStr)
      .order("created_at", { ascending: false })
      .range((page - 1) * limit, page * limit - 1);

    // Apply filters
    if (filters.storeId) query = query.eq("store_id", filters.storeId);
    if (filters.categoryId) {
      query = query.eq('coupon_categories.category_id', filters.categoryId);
    }
    if (filters.isFeatured) query = query.eq("is_featured", filters.isFeatured);
    if (filters.isExclusive) query = query.eq("is_exclusive", filters.isExclusive);
    if (filters.active) {
      // Show coupons where expiry_date is in the future OR expiry_date is NULL (never expires)
      query = query.or(`expiry_date.gt.${new Date().toISOString()},expiry_date.is.null`);
    }

    const { data, error, count } = await query;

    // Map categories to category_ids array
    const mapped = (data || []).map(coupon => ({
      ...coupon,
      category_ids: (coupon.coupon_categories || []).map(cc => cc.category_id),
      categories: (coupon.coupon_categories || []).map(cc => cc.category)
    }));

    if (cacheKey && data && !error) {
      await kvSet(cacheKey, mapped);
    }

    return { data: mapped, error, count };
  },

  /**
   * Fetch a single coupon by ID with its categories.
   */
  async getById(id) {
    // Cache popular coupon details (by id)
    if (isCloudflare) {
      const cached = await kvGet(`coupon:detail:${id}`);
      if (cached) {
        console.log('Using KV cached coupon detail for id:', id);
        return { data: cached, error: null };
      }
    }
    const { data, error } = await supabase
      .from("coupons")
      .select(`*, store:stores(*), coupon_categories:coupon_categories(category_id, category:categories(*))`)
      .eq("id", id)
      .single();

    if (!data) return { data: null, error };
    const mapped = {
      ...data,
      category_ids: (data.coupon_categories || []).map(cc => cc.category_id),
      categories: (data.coupon_categories || []).map(cc => cc.category)
    };
    if (isCloudflare && data) {
      await kvSet(`coupon:detail:${id}`, mapped);
    }
    return { data: mapped, error };
  },

  /**
   * Create a new coupon and assign categories via join table.
   * Expects coupon.category_ids as an array of category IDs.
   */
  async create(coupon) {
    // Extract category_ids and remove from main insert
    const { category_ids = [], ...couponData } = coupon;
    const { data, error } = await supabase
      .from("coupons")
      .insert([couponData])
      .select();

    // Invalidate coupon list caches
    if (isCloudflare) {
      if (data && data[0]?.store_id) await kvDelete(`coupons:store:${data[0].store_id}`);
      if (category_ids && category_ids.length) {
        for (const cid of category_ids) await kvDelete(`coupons:category:${cid}`);
      }
    }

    if (error || !data || !data[0]) return { data: null, error };
    const couponId = data[0].id;
    // Insert into join table
    if (category_ids.length > 0) {
      const joinRows = category_ids.map(cid => ({ coupon_id: couponId, category_id: cid }));
      const { error: joinError } = await supabase.from('coupon_categories').insert(joinRows);
      if (joinError) return { data: null, error: joinError };
    }
    // Return full coupon with categories
    return this.getById(couponId);
  },

  /**
   * Update a coupon and its categories via join table.
   * Expects updates.category_ids as an array of category IDs.
   */
  async update(id, updates) {
    const { category_ids = null, ...couponUpdates } = updates;
    const { data, error } = await supabase
      .from("coupons")
      .update(couponUpdates)
      .eq("id", id)
      .select();

    // Invalidate coupon list caches
    if (isCloudflare) {
      if (data && data[0]?.store_id) await kvDelete(`coupons:store:${data[0].store_id}`);
      if (category_ids && Array.isArray(category_ids)) {
        for (const cid of category_ids) await kvDelete(`coupons:category:${cid}`);
      }
      await kvDelete(`coupon:detail:${id}`);
    }

    if (error) return { data: null, error };
    // Update join table if category_ids provided
    if (Array.isArray(category_ids)) {
      // Remove existing
      await supabase.from('coupon_categories').delete().eq('coupon_id', id);
      // Insert new
      if (category_ids.length > 0) {
        const joinRows = category_ids.map(cid => ({ coupon_id: id, category_id: cid }));
        const { error: joinError } = await supabase.from('coupon_categories').insert(joinRows);
        if (joinError) return { data: null, error: joinError };
      }
    }
    // Return updated coupon with categories
    return this.getById(id);
  },

  async delete(id) {
    // Delete join table rows first for integrity
    await supabase.from('coupon_categories').delete().eq('coupon_id', id);
    const { error } = await supabase.from("coupons").delete().eq("id", id);

    // Invalidate all coupon caches (brute force)
    if (isCloudflare) {
      await kvDelete(`coupon:detail:${id}`);
      // Optionally: scan and delete all coupons:store:* and coupons:category:* if needed
    }

    return { error };
  },

  async trackClick(couponId) {
    // Track clicks for analytics
    const { data, error } = await supabase
      .from("coupon_clicks")
      .insert([{ coupon_id: couponId, clicked_at: new Date().toISOString() }]);
    return { data, error };
  },
};

// Data service for stores
export const storesService = {
  // Simple in-memory cache (fallback)
  _storesCache: null,
  _cacheTimestamp: null,
  _cacheDuration: 5 * 60 * 1000, // 5 minutes

  async getAll() {
    // Try Cloudflare KV first
    if (isCloudflare) {
      const cached = await kvGet('stores:all');
      if (cached) {
        console.log('Using KV cached stores data');
        return { data: cached, error: null };
      }
    } else {
      // Fallback to in-memory cache
      const now = Date.now();
      if (this._storesCache && this._cacheTimestamp && (now - this._cacheTimestamp < this._cacheDuration)) {
        console.log('Using in-memory cached stores data');
        return { data: [...this._storesCache], error: null };
      }
    }

    // Fetch from Supabase
    console.log('Fetching stores from Supabase');
    const { data, error } = await supabase
      .from("stores")
      .select(`*, coupons_count:coupons(count)`)
      .order("name", { ascending: true });

    if (data && !error) {
      data.forEach(store => {
        store.coupons_count = store.coupons_count && store.coupons_count[0] ? store.coupons_count[0].count : 0;
      });
      // Update caches
      if (isCloudflare) await kvSet('stores:all', data);
      else {
        this._storesCache = [...data];
        this._cacheTimestamp = Date.now();
      }
    }
    return { data, error };
  },

  async getById(id) {
    // Not cached (rarely used)
    const { data, error } = await supabase
      .from("stores")
      .select("*")
      .eq("id", id)
      .single();
    return { data, error };
  },

  async getBySlug(slug) {
    // Try Cloudflare KV first
    if (isCloudflare) {
      const cached = await kvGet(`stores:slug:${slug}`);
      if (cached) {
        console.log('Using KV cached store for slug:', slug);
        return { data: { ...cached }, error: null };
      }
    } else if (this._storesCache) {
      const cachedStore = this._storesCache.find(store => store.url_slug === slug);
      if (cachedStore) {
        console.log('Using in-memory cached store for slug:', slug);
        return { data: { ...cachedStore }, error: null };
      }
    }
    // Fetch from Supabase
    const { data, error } = await supabase
      .from("stores")
      .select(`*, coupons_count:coupons(count), store_categories:store_category(*, category:categories(*))`)
      .eq("url_slug", slug)
      .single();
    if (data) {
      data.coupons_count = data.coupons_count && data.coupons_count[0] ? data.coupons_count[0].count : 0;
      data.categories = (data.store_categories || []).map(sc => sc.category).filter(Boolean);
      if (isCloudflare) await kvSet(`stores:slug:${slug}`, data);
    }
    return { data, error };
  },

  async getFeatured() {
    // Try Cloudflare KV first
    if (isCloudflare) {
      const cached = await kvGet('stores:featured');
      if (cached) {
        console.log('Using KV cached featured stores');
        return { data: cached, error: null };
      }
    } else if (this._storesCache) {
      const featuredStores = this._storesCache.filter(store => store.is_featured === true);
      if (featuredStores.length > 0) {
        console.log('Using in-memory cached featured stores');
        return { data: [...featuredStores], error: null };
      }
    }
    // Fetch from Supabase
    const { data, error } = await supabase
      .from("stores")
      .select(`*, coupons_count:coupons(count)`)
      .eq("is_featured", true)
      .order("name", { ascending: true });
    if (data) {
      data.forEach(store => {
        store.coupons_count = store.coupons_count && store.coupons_count[0] ? store.coupons_count[0].count : 0;
      });
      if (isCloudflare) await kvSet('stores:featured', data);
    }
    return { data, error };
  },

  async create(store) {
    const { data, error } = await supabase
      .from("stores")
      .insert([store])
      .select();
    // Invalidate caches
    if (isCloudflare) {
      await kvDelete('stores:all');
      await kvDelete('stores:featured');
      if (data && data[0]?.url_slug) await kvDelete(`stores:slug:${data[0].url_slug}`);
    } else {
      this._storesCache = null;
      this._cacheTimestamp = null;
    }
    return { data, error };
  },

  async update(id, updates) {
    const { data, error } = await supabase
      .from("stores")
      .update(updates)
      .eq("id", id)
      .select();
    // Invalidate caches
    if (isCloudflare) {
      await kvDelete('stores:all');
      await kvDelete('stores:featured');
      if (data && data[0]?.url_slug) await kvDelete(`stores:slug:${data[0].url_slug}`);
    } else {
      this._storesCache = null;
      this._cacheTimestamp = null;
    }
    return { data, error };
  },

  async delete(id) {
    const { error } = await supabase.from("stores").delete().eq("id", id);
    // Invalidate caches (brute force, since we don't know slug)
    if (isCloudflare) {
      await kvDelete('stores:all');
      await kvDelete('stores:featured');
      // Optionally: scan and delete all stores:slug:* if needed
    } else {
      this._storesCache = null;
      this._cacheTimestamp = null;
    }
    return { error };
  },

  /**
   * Get similar stores by category overlap, excluding the current store.
   * @param {number} storeId - The current store's ID
   * @param {number[]} categoryIds - Array of category IDs for the current store
   * @param {number} limit - Max number of similar stores to return
   * @returns {Promise<{data: Array, error: Error}>}
   */
  async getSimilarStores(storeId, categoryIds, limit = 6) {
    if (!Array.isArray(categoryIds) || categoryIds.length === 0) {
      return { data: [], error: null };
    }
    // Try Cloudflare KV first
    const kvKey = `stores:similar:${storeId}:${categoryIds.join(',')}:${limit}`;
    if (isCloudflare) {
      const cached = await kvGet(kvKey);
      if (cached) {
        return { data: cached, error: null };
      }
    }
    // Query stores that share at least one category, excluding the current store
    const { data, error } = await supabase
      .from('stores')
      .select('*, coupons_count:coupons(count), store_categories:store_category(*, category:categories(*))')
      .neq('id', storeId)
      .in('id',
        (
          await supabase
            .from('store_category')
            .select('store_id')
            .in('category_id', categoryIds)
        ).data?.map(sc => sc.store_id) || []
      );
    if (error) return { data: [], error };
    // Remove duplicates and sort by coupon count desc
    const uniqueStores = [];
    const seen = new Set();
    (data || []).forEach(store => {
      if (!seen.has(store.id)) {
        store.coupons_count = store.coupons_count && store.coupons_count[0] ? store.coupons_count[0].count : 0;
        store.categories = (store.store_categories || []).map(sc => sc.category).filter(Boolean);
        seen.add(store.id);
        uniqueStores.push(store);
      }
    });
    uniqueStores.sort((a, b) => (b.coupons_count || 0) - (a.coupons_count || 0));
    const result = uniqueStores.slice(0, limit);
    if (isCloudflare) await kvSet(kvKey, result, 300);
    return { data: result, error: null };
  },
};

// Data service for categories
export const categoriesService = {
  async getAll() {
    // Try Cloudflare KV first
    if (isCloudflare) {
      const cached = await kvGet('categories:all');
      if (cached) {
        console.log('Using KV cached categories data');
        return { data: cached, error: null };
      }
    }
    // Fetch from Supabase
    const { data, error } = await supabase
      .from("categories")
      .select("*, coupons:coupons(count)")
      .order("name", { ascending: true });
    // Map coupon_count from aggregate
    const categoriesWithCount = data?.map(cat => ({
      ...cat,
      coupon_count: cat.coupons && cat.coupons[0] ? cat.coupons[0].count : 0
    })) || [];
    if (data && !error && isCloudflare) {
      await kvSet('categories:all', categoriesWithCount);
    }
    return { data: categoriesWithCount, error };
  },

  async getById(id) {
    // Try Cloudflare KV first
    if (isCloudflare) {
      const cached = await kvGet(`categories:id:${id}`);
      if (cached) {
        console.log('Using KV cached category by id:', id);
        return { data: cached, error: null };
      }
    }
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .eq("id", id)
      .single();
    if (data && isCloudflare) {
      await kvSet(`categories:id:${id}`, data);
    }
    return { data, error };
  },

  async getBySlug(slug) {
    // Try Cloudflare KV first
    if (isCloudflare) {
      const cached = await kvGet(`categories:slug:${slug}`);
      if (cached) {
        console.log('Using KV cached category by slug:', slug);
        return { data: cached, error: null };
      }
    }
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .ilike("url_slug", slug)
      .single();
    if (data && isCloudflare) {
      await kvSet(`categories:slug:${slug}`, data);
    }
    return { data, error };
  },
  
  async getTopCategories(limit = 5) {
    // Try Cloudflare KV first
    if (isCloudflare) {
      const cached = await kvGet(`categories:top:${limit}`);
      if (cached) {
        console.log('Using KV cached top categories:', limit);
        return { data: cached, error: null };
      }
    }
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("name", { ascending: true })
      .limit(limit);
    if (data && isCloudflare) {
      await kvSet(`categories:top:${limit}`, data);
    }
    return { data, error };
  },

  async create(category) {
    const { data, error } = await supabase
      .from("categories")
      .insert([category])
      .select();
    // Invalidate caches
    if (isCloudflare) {
      await kvDelete('categories:all');
      // Optionally: delete all categories:top:* and categories:slug:* if needed
    }
    return { data, error };
  },

  async update(id, updates) {
    const { data, error } = await supabase
      .from("categories")
      .update(updates)
      .eq("id", id)
      .select();
    // Invalidate caches
    if (isCloudflare) {
      await kvDelete('categories:all');
      await kvDelete(`categories:id:${id}`);
      // Optionally: delete all categories:top:* and categories:slug:* if needed
    }
    return { data, error };
  },

  async delete(id) {
    const { error } = await supabase.from("categories").delete().eq("id", id);
    // Invalidate caches
    if (isCloudflare) {
      await kvDelete('categories:all');
      await kvDelete(`categories:id:${id}`);
      // Optionally: delete all categories:top:* and categories:slug:* if needed
    }
    return { error };
  },
};

// Analytics service
export const analyticsService = {
  async getCouponClicks(period = "7d") {
    // Try Cloudflare KV first
    if (isCloudflare) {
      const cached = await kvGet(`analytics:couponClicks:${period}`);
      if (cached) {
        console.log('Using KV cached coupon clicks for period:', period);
        return { data: cached, error: null };
      }
    }
    let dateFilter;

    const now = new Date();
    if (period === "7d") {
      dateFilter = new Date(now.setDate(now.getDate() - 7)).toISOString();
    } else if (period === "30d") {
      dateFilter = new Date(now.setDate(now.getDate() - 30)).toISOString();
    } else if (period === "1y") {
      dateFilter = new Date(
        now.setFullYear(now.getFullYear() - 1),
      ).toISOString();
    }

    const { data, error } = await supabase
      .from("coupon_clicks")
      .select("coupon_id, clicked_at, coupons(title, store_id, stores(name))")
      .gt("clicked_at", dateFilter);
    if (data && isCloudflare) {
      await kvSet(`analytics:couponClicks:${period}`, data, 60); // 1 min TTL for analytics
    }
    return { data, error };
  },

  async getTopStores(limit = 10) {
    // Try Cloudflare KV first
    if (isCloudflare) {
      const cached = await kvGet(`analytics:topStores:${limit}`);
      if (cached) {
        console.log('Using KV cached top stores:', limit);
        return { data: cached, error: null };
      }
    }
    const { data, error } = await supabase
      .from("coupon_clicks")
      .select("coupons(store_id, stores(id, name))")
      .limit(1000); // Get a large sample

    // Process the data to count by store
    let topStores = [];
    if (data) {
      const storeCount = {};

      data.forEach((click) => {
        const storeId = click.coupons?.store_id;
        if (storeId) {
          storeCount[storeId] = (storeCount[storeId] || 0) + 1;
        }
      });

      // Convert to array and sort
      topStores = Object.entries(storeCount)
        .map(([storeId, count]) => ({
          storeId: parseInt(storeId),
          count,
          storeName: data.find(
            (click) => click.coupons?.store_id === parseInt(storeId),
          )?.coupons?.stores?.name,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);
      if (isCloudflare) {
        await kvSet(`analytics:topStores:${limit}`, topStores, 60); // 1 min TTL for analytics
      }
    }

    return { data: topStores, error };
  },
};

// Data service for banner ads
export const bannerService = {
  // Simple in-memory cache (fallback)
  _bannersCache: null,
  _cacheTimestamp: null,
  _cacheDuration: 5 * 60 * 1000, // 5 minutes
  
  /**
   * Get all banner ads or filter by position
   * @param {string} position - Optional filter by position ('main_slider' or 'side_banner')
   * @returns {Promise<{data: Array, error: Error}>}
   */
  async getBanners(position = null) {
    // Try Cloudflare KV first
    const kvKey = position ? `banners:${position}` : 'banners:all';
    if (isCloudflare) {
      const cached = await kvGet(kvKey);
      if (cached) {
        console.log('Using KV cached banners data');
        return { data: cached, error: null };
      }
    } else {
      const now = Date.now();
      if (this._bannersCache && this._cacheTimestamp && (now - this._cacheTimestamp < this._cacheDuration)) {
        console.log('Using in-memory cached banners data');
        if (position) {
          const filteredBanners = this._bannersCache.filter(banner => banner.position === position);
          return { data: filteredBanners, error: null };
        }
        return { data: [...this._bannersCache], error: null };
      }
    }
    // Fetch from Supabase
    let query = supabase
      .from("banner_ads")
      .select("*")
      .order("display_order", { ascending: true });
    if (position) {
      query = query.eq("position", position);
    }
    const { data, error } = await query;
    if (data && !error) {
      if (isCloudflare) await kvSet(kvKey, data);
      else {
        this._bannersCache = [...data];
        this._cacheTimestamp = Date.now();
      }
    }
    return { data, error };
  },
  
  /**
   * Get main slider banners
   * @returns {Promise<{data: Array, error: Error}>}
   */
  async getMainSliderBanners() {
    return this.getBanners('main_slider');
  },
    /**
   * Get side banner
   * @returns {Promise<{data: Array, error: Error}>}
   */
  async getSideBanner() {
    // Return all active side banners
    return this.getBanners('side_banner');
  },
  
  /**
   * Create a new banner ad
   * @param {Object} banner - Banner data
   * @returns {Promise<{data: Object, error: Error}>}
   */
  async create(banner) {
    const { data, error } = await supabase
      .from("banner_ads")
      .insert([banner])
      .select();
      
    // Clear cache on successful create
    if (data && !error) {
      if (isCloudflare) {
        await kvDelete('banners:all');
        await kvDelete('banners:main_slider');
        await kvDelete('banners:side_banner');
      } else {
        this._bannersCache = null;
        this._cacheTimestamp = null;
      }
    }
    
    return { data, error };
  },
  
  /**
   * Update an existing banner ad
   * @param {number} id - Banner ID
   * @param {Object} updates - Banner data updates
   * @returns {Promise<{data: Object, error: Error}>}
   */
  async update(id, updates) {
    const { data, error } = await supabase
      .from("banner_ads")
      .update(updates)
      .eq("id", id)
      .select();
      
    // Clear cache on successful update
    if (data && !error) {
      if (isCloudflare) {
        await kvDelete('banners:all');
        await kvDelete('banners:main_slider');
        await kvDelete('banners:side_banner');
      } else {
        this._bannersCache = null;
        this._cacheTimestamp = null;
      }
    }
    
    return { data, error };
  },
  
  /**
   * Delete a banner ad
   * @param {number} id - Banner ID
   * @returns {Promise<{error: Error}>}
   */
  async delete(id) {
    const { error } = await supabase
      .from("banner_ads")
      .delete()
      .eq("id", id);
      
    // Clear cache on successful delete
    if (!error) {
      if (isCloudflare) {
        await kvDelete('banners:all');
        await kvDelete('banners:main_slider');
        await kvDelete('banners:side_banner');
      } else {
        this._bannersCache = null;
        this._cacheTimestamp = null;
      }
    }
    
    return { error };
  },
  
  /**
   * Track banner click for analytics
   * @param {number} bannerId - Banner ID
   * @returns {Promise<{data: Object, error: Error}>}
   */
  async trackClick(bannerId) {
    try {
      const { error } = await supabase
        .from('banner_ads')
        .update({ click_count: supabase.rpc ? undefined : 0 })
        .eq('id', bannerId);
      
      await supabase.rpc('increment_banner_clicks', { banner_id: bannerId }).catch(() => {
        // RPC may not exist; silently ignore
      });
    } catch {}
    return { data: true, error: null };
  }
};

// Settings/static content caching example
export const settingsService = {
  async getSettings() {
    if (isCloudflare) {
      const cached = await kvGet('settings:site');
      if (cached) {
        console.log('Using KV cached site settings');
        return { data: cached, error: null };
      }
    }
    const { data, error } = await supabase
      .from('settings')
      .select('*')
      .single();
    if (data && isCloudflare) {
      await kvSet('settings:site', data, 3600); // 1 hour TTL
    }
    return { data, error };
  },
  async updateSettings(updates) {
    const { data, error } = await supabase
      .from('settings')
      .update(updates)
      .eq('id', updates.id)
      .select();
    if (isCloudflare) {
      await kvDelete('settings:site');
    }
    return { data, error };
  }
};

// Static pages caching example
export const staticPagesService = {
  async getPage(slug) {
    if (isCloudflare) {
      const cached = await kvGet(`staticpage:${slug}`);
      if (cached) {
        console.log('Using KV cached static page:', slug);
        return { data: cached, error: null };
      }
    }
    const { data, error } = await supabase
      .from('static_pages')
      .select('*')
      .eq('slug', slug)
      .single();
    if (data && isCloudflare) {
      await kvSet(`staticpage:${slug}`, data, 3600); // 1 hour TTL
    }
    return { data, error };
  },
  async updatePage(slug, updates) {
    const { data, error } = await supabase
      .from('static_pages')
      .update(updates)
      .eq('slug', slug)
      .select();
    if (isCloudflare) {
      await kvDelete(`staticpage:${slug}`);
    }
    return { data, error };
  }
};

// Image/CDN URL mappings caching example
export const imageMappingService = {
  async getImageUrl(key) {
    if (isCloudflare) {
      const cached = await kvGet(`imageurl:${key}`);
      if (cached) {
        return { url: cached, error: null };
      }
    }
    const { data, error } = await supabase
      .from('image_mappings')
      .select('url')
      .eq('key', key)
      .single();
    if (data && data.url && isCloudflare) {
      await kvSet(`imageurl:${key}`, data.url, 86400); // 1 day TTL
    }
    return { url: data?.url, error };
  },
  async updateImageUrl(key, url) {
    const { data, error } = await supabase
      .from('image_mappings')
      .update({ url })
      .eq('key', key)
      .select();
    if (isCloudflare) {
      await kvDelete(`imageurl:${key}`);
    }
    return { data, error };
  }
};

// Precomputed stats/aggregates caching example
export const statsService = {
  async getStats() {
    if (isCloudflare) {
      const cached = await kvGet('stats:site');
      if (cached) {
        console.log('Using KV cached site stats');
        return { data: cached, error: null };
      }
    }
    // Example: total coupons, total stores, total categories
    const [{ count: couponsCount }, { count: storesCount }, { count: categoriesCount }] = await Promise.all([
      supabase.from('coupons').select('*', { count: 'exact', head: true }),
      supabase.from('stores').select('*', { count: 'exact', head: true }),
      supabase.from('categories').select('*', { count: 'exact', head: true })
    ]);
    const stats = {
      total_coupons: couponsCount || 0,
      total_stores: storesCount || 0,
      total_categories: categoriesCount || 0
    };
    if (isCloudflare) {
      await kvSet('stats:site', stats, 300); // 5 min TTL
    }
    return { data: stats, error: null };
  },
  async invalidateStats() {
    if (isCloudflare) {
      await kvDelete('stats:site');
    }
  }
};

export const newsletterService = {
  async subscribe(email) {
    const { data, error } = await supabase
      .from('newsletter_subscribers')
      .upsert({ email, subscribed_at: new Date().toISOString(), is_active: true }, { onConflict: 'email' })
      .select()
      .single();
    if (error) throw error;
    return data;
  },
  async unsubscribe(email) {
    const { error } = await supabase
      .from('newsletter_subscribers')
      .update({ is_active: false })
      .eq('email', email);
    if (error) throw error;
  }
};

export default {
  auth,
  couponsService,
  storesService,
  categoriesService,
  analyticsService,
  bannerService,
  settingsService,
  staticPagesService,
  imageMappingService,
  statsService,
  newsletterService,
};
